import { useApolloClient } from '@apollo/client';
import { FC } from 'react';
import {
  GetLineDetailsByIdDocument,
  GetLineDetailsByIdQuery,
  GetLineDetailsByIdQueryVariables,
  GetRouteDetailsByIdDocument,
  GetRouteDetailsByIdQuery,
  GetRouteDetailsByIdQueryVariables,
} from '../../../../generated/graphql';
import {
  Operation,
  finishRouteMetadataEditingAction,
  resetRouteCreatingAction,
  selectEditedRouteData,
  selectMapRouteEditor,
  setDraftRouteJourneyPatternAction,
  setLineInfoAction,
  setRouteMetadataFormOpenAction,
  useAppDispatch,
  useAppSelector,
  useLoader,
} from '../../../../redux';
import { LoadingState } from '../../../../types';
import { stopInJourneyPatternFieldsToRemove } from '../../../../utils';
import {
  RouteFormState,
  routeFormSchema,
} from '../../../LinesAndRoutes/Common';
import { EditRouteModal } from './EditRouteModal';

function areFormValuesValid(formData?: Partial<RouteFormState>) {
  return routeFormSchema.safeParse(formData).success;
}

export const EditRouteMetadataLayer: FC = () => {
  const apollo = useApolloClient();
  const dispatch = useAppDispatch();
  const { setLoadingState: setRouteDrawLoadingState } = useLoader(
    Operation.PrepareRouteDraw,
  );
  const { isRouteMetadataFormOpen } = useAppSelector(selectMapRouteEditor);
  const {
    templateRouteId,
    metaData: editedRouteMetadata,
    lineInfo: editedRouteLineInfo,
  } = useAppSelector(selectEditedRouteData);

  const onClose = () => {
    dispatch(setRouteMetadataFormOpenAction(false));

    const alreadyHaveValidFormValues = areFormValuesValid(editedRouteMetadata);

    // In case route metadata form has not yet been submitted with valid values,
    // closing the metadata form will also cancel the route creation.
    // Otherwise, clicking close on the modal will only close the modal
    if (!alreadyHaveValidFormValues) {
      dispatch(resetRouteCreatingAction());
    }
  };

  const onSuccess = async (formData: RouteFormState) => {
    // Keep map loader visible from modal save until DrawRouteLayer confirms draw is ready.
    setRouteDrawLoadingState(LoadingState.HighPriority);

    try {
      // The line might have been changed by the user, so have to refresh its data in the redux store
      const results = await apollo.query<
        GetLineDetailsByIdQuery,
        GetLineDetailsByIdQueryVariables
      >({
        query: GetLineDetailsByIdDocument,
        variables: { line_id: formData.onLineId },
      });
      if (!results.data.route_line_by_pk) {
        throw new Error("Couldn't get line details!");
      }

      dispatch(setLineInfoAction(results.data.route_line_by_pk));
      dispatch(finishRouteMetadataEditingAction(formData));

      /**
       * Get journey pattern stop metadata (e.g. via info) from template route
       */
      if (templateRouteId) {
        const routeDetailsResult = await apollo.query<
          GetRouteDetailsByIdQuery,
          GetRouteDetailsByIdQueryVariables
        >({
          query: GetRouteDetailsByIdDocument,
          variables: { routeId: templateRouteId },
        });
        if (!routeDetailsResult.data.route_route_by_pk) {
          throw new Error("Can't find route and line details");
        }

        const newJourneyPatternStops =
          routeDetailsResult.data.route_route_by_pk.route_journey_patterns[0].ordered_scheduled_stop_point_in_journey_patterns.map(
            (stopInJourneyPattern) => ({
              ...stopInJourneyPattern,
              ...stopInJourneyPatternFieldsToRemove,
            }),
          );

        dispatch(
          setDraftRouteJourneyPatternAction({ stops: newJourneyPatternStops }),
        );
      }
    } catch (error) {
      // DrawRouteLayer is not guaranteed to mount on failed submit, so clear prepare-draw loader here.
      setRouteDrawLoadingState(LoadingState.NotLoading);
      throw error;
    }
  };

  // In can we already have some route metadata, fill it in. Also fill in the line id if we already
  // have it
  const defaultValues: Partial<RouteFormState> = {
    ...editedRouteMetadata,
    onLineId: editedRouteLineInfo?.line_id,
  };

  if (!isRouteMetadataFormOpen) {
    return null;
  }

  return (
    <EditRouteModal
      defaultValues={defaultValues}
      onSuccess={onSuccess}
      onCancel={onClose}
      onClose={onClose}
    />
  );
};
